import {
  VENDOR_APPLICATION_DELIVERY_CYCLE_LABELS,
  VENDOR_APPLICATION_DELIVERY_SCOPE_LABELS,
  VENDOR_APPLICATION_FREIGHT_RULE_LABELS,
  VENDOR_APPLICATION_INVOICE_DELIVERY_LABELS,
  VENDOR_APPLICATION_INVOICE_FORMAT_LABELS,
  VENDOR_APPLICATION_PAYMENT_METHOD_LABELS,
  VENDOR_APPLICATION_PAYMENT_TERM_LABELS,
  VENDOR_APPLICATION_RETURN_CONDITION_LABELS,
  VENDOR_APPLICATION_RETURN_FREIGHT_LABELS,
  VENDOR_APPLICATION_RETURN_HANDLING_LABELS,
  VENDOR_APPLICATION_WEEKDAY_LABELS,
  VENDOR_DOCUMENT_TYPE_LABELS,
  VENDOR_PRODUCT_CATEGORY_LABELS,
  VENDOR_STATUS_LABELS,
  VENDOR_DELIVERY_CYCLES,
  VENDOR_FREIGHT_RULES,
  VENDOR_INVOICE_DELIVERY_METHODS,
  VENDOR_INVOICE_FORMATS,
  VENDOR_RETURN_CONDITION_TYPES,
  VENDOR_RETURN_FREIGHT_RESPONSIBILITIES,
  VENDOR_RETURN_HANDLING_METHODS,
  VENDOR_WEEKDAYS,
} from './constants';

export type VendorLabelMap = Readonly<Record<string, string>>;

/** 廠商端與廠商 PDF 共用的繁體中文顯示對照。資料庫／API 仍保存英文代碼。 */
export const vendorLabels = {
  paymentTerm: VENDOR_APPLICATION_PAYMENT_TERM_LABELS,
  paymentMethod: VENDOR_APPLICATION_PAYMENT_METHOD_LABELS,
  deliveryScope: VENDOR_APPLICATION_DELIVERY_SCOPE_LABELS,
  deliveryCycle: {
    ...VENDOR_APPLICATION_DELIVERY_CYCLE_LABELS,
    BY_ORDER: '依訂單通知',
  },
  weekday: VENDOR_APPLICATION_WEEKDAY_LABELS,
  freightRule: {
    ...VENDOR_APPLICATION_FREIGHT_RULE_LABELS,
    FREE_SHIPPING: '免運',
    FREE_OVER_AMOUNT: '達指定金額免運',
    FIXED_FEE: '固定運費',
    ACTUAL_LOGISTICS_FEE: '依實際物流費',
  },
  returnHandling: {
    ...VENDOR_APPLICATION_RETURN_HANDLING_LABELS,
    NO_RETURN_EXCHANGE: '不接受退換貨',
  },
  returnCondition: {
    ...VENDOR_APPLICATION_RETURN_CONDITION_LABELS,
    DEFECTIVE: '瑕疵',
    UNSUITABLE: '吃不合',
  },
  returnFreight: {
    ...VENDOR_APPLICATION_RETURN_FREIGHT_LABELS,
    MUTUAL_AGREEMENT: '雙方協議',
  },
  invoiceFormat: {
    ...VENDOR_APPLICATION_INVOICE_FORMAT_LABELS,
    E_INVOICE: '電子發票',
    PAPER_TWO_COPY: '紙本二聯式',
    PAPER_THREE_COPY: '紙本三聯式',
    COMPUTER_UNIFORM_INVOICE: '電子計算機統一發票',
    CASH_REGISTER_INVOICE: '收銀機統一發票',
  },
  invoiceDelivery: {
    ...VENDOR_APPLICATION_INVOICE_DELIVERY_LABELS,
    WITH_DELIVERY: '隨貨附發票',
  },
  category: VENDOR_PRODUCT_CATEGORY_LABELS,
  attachmentType: {
    ...VENDOR_DOCUMENT_TYPE_LABELS,
    BUSINESS_REGISTRATION: '公司／商業登記資料',
    TAX_ID_DOCUMENT: '統一編號相關證明',
    BANK_ACCOUNT_PROOF: '銀行帳戶證明',
    CATALOG: '公司介紹／型錄',
    LIABILITY_INSURANCE: '產品責任保險資料',
  },
  vendorStatus: VENDOR_STATUS_LABELS,
  changeRequestStatus: {
    DRAFT: '草稿',
    SUBMITTED: '已送出',
    PENDING: '待處理',
    NEEDS_INFO: '要求補充',
    SUPPLEMENT_REQUIRED: '要求補充',
    RETURNED: '退回修改',
    APPROVED: '已核准',
    REJECTED: '不同意',
    APPLIED: '已套用',
  },
  changeRequestField: {
    'business.company_name': '公司名稱',
    'business.company_short_name': '公司簡稱',
    'business.tax_id': '統一編號',
    'business.phone': '公司電話',
    'business.address': '公司地址',
    'business.company_email': '公司 Email',
    'business.website': '公司網站',
    'business.company_fax': '傳真',
    'business.company_intro': '公司簡介',
    'business.notes': '備註',
    'business.principal_brands': '主要代理／品牌',
    'business.supplied_product_categories': '供應商品類',
    'contact.contact_name': '聯絡人姓名',
    'contact.contact_job_title': '聯絡人職稱',
    'contact.contact_phone': '聯絡電話',
    'contact.contact_email': '聯絡 Email',
    'contact.vendor_closing_day': '廠商結帳日',
    'contact.daily_order_cutoff_time': '廠商每日訂單截止時間',
    'payment_terms.payment_term': '付款條件',
    'payment_terms.monthly_payment_days': '月結天數',
    'payment_terms.payment_method': '付款方式',
    'payment_terms.payment_method_other': '其他付款方式說明',
    'delivery_terms.delivery_scope': '配送地點',
    'delivery_terms.delivery_cycle': '廠商配送週期',
    'delivery_terms.delivery_weekdays': '固定配送星期',
    'delivery_terms.minimum_order_amount': '最低訂購金額',
    'delivery_terms.minimum_order_quantity': '最低訂購數量',
    'delivery_terms.freight_rule': '廠商運費規則',
    'return_terms.return_scope': '退貨地點',
    'return_terms.return_handling_method': '退貨／換貨處理方式',
    'return_terms.return_condition_types': '退貨／換貨成立條件',
    'return_terms.return_condition_description': '退貨／換貨詳細說明',
    'return_terms.return_freight_responsibility': '退貨／換貨運費負擔',
    'invoice_terms.invoice_format': '發票格式',
    'invoice_terms.invoice_delivery_method': '發票寄送方式',
    'invoice_terms.invoice_contact_name': '發票聯絡人',
    'invoice_terms.invoice_contact_phone': '發票聯絡電話',
    'invoice_terms.invoice_email': '發票 Email',
    'invoice_terms.reconciliation_contact_name': '對帳聯絡人',
    'invoice_terms.reconciliation_contact_phone': '對帳聯絡電話',
    'invoice_terms.reconciliation_email': '對帳 Email',
    bank_details: '銀行資料',
  },
} as const;

export interface VendorOption {
  value: string;
  label: string;
}

function optionList(codes: readonly string[], labels: VendorLabelMap): VendorOption[] {
  return codes.map((value) => ({ value, label: labels[value] ?? value }));
}

/**
 * 廠商申請、正式廠商主檔、自助資料與變更申請共用的選項清單。
 * vendorLabels 另外保留舊代碼的相容對照，但畫面只應使用這組 canonical options。
 */
export const vendorOptions = {
  deliveryCycles: optionList(VENDOR_DELIVERY_CYCLES, vendorLabels.deliveryCycle),
  freightRules: optionList(VENDOR_FREIGHT_RULES, vendorLabels.freightRule),
  invoiceDeliveryMethods: optionList(VENDOR_INVOICE_DELIVERY_METHODS, vendorLabels.invoiceDelivery),
  invoiceFormats: optionList(VENDOR_INVOICE_FORMATS, vendorLabels.invoiceFormat),
  returnConditionTypes: optionList(VENDOR_RETURN_CONDITION_TYPES, vendorLabels.returnCondition),
  returnFreightResponsibilities: optionList(
    VENDOR_RETURN_FREIGHT_RESPONSIBILITIES,
    vendorLabels.returnFreight,
  ),
  returnHandlingMethods: optionList(VENDOR_RETURN_HANDLING_METHODS, vendorLabels.returnHandling),
  weekdays: optionList(VENDOR_WEEKDAYS, vendorLabels.weekday),
} as const;

function normalized(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

function listValue(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(normalized).filter(Boolean);
  const text = normalized(value);
  if (!text) return [];
  if (text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text) as unknown;
      if (Array.isArray(parsed)) return parsed.map(normalized).filter(Boolean);
    } catch {
      // Fall through to the human-readable separator handling below.
    }
  }
  return text
    .split(/[、,，]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function displayVendorValue(value: unknown, empty = '未填寫'): string {
  if (value === null || value === undefined || value === '') return empty;
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (Array.isArray(value)) return value.length ? value.join('、') : '未設定';
  if (typeof value === 'number' && !Number.isFinite(value)) return empty;
  if (typeof value === 'object') return empty;
  return String(value);
}

export function labelVendorCode(value: unknown, labels: VendorLabelMap, empty = '未填寫'): string {
  const code = normalized(value);
  if (!code) return empty;
  if (labels[code]) return labels[code];
  const existingLabel = Object.values(labels).find((label) => label === code);
  return existingLabel ?? '未設定';
}

export function parseVendorCode(value: unknown, labels: VendorLabelMap): string {
  return codeList(value, labels)[0] ?? '';
}

function labelList(value: unknown, labels: VendorLabelMap, empty = '未設定'): string {
  const items = listValue(value).map((item) => labelVendorCode(item, labels, empty));
  return items.length ? items.join('、') : empty;
}

function codeList(value: unknown, labels: VendorLabelMap): string[] {
  const reverse = new Map<string, string>();
  Object.entries(labels).forEach(([code, label]) => {
    if (!reverse.has(label)) reverse.set(label, code);
  });
  return listValue(value)
    .map((item) => reverse.get(item) ?? item.toUpperCase())
    .filter(Boolean);
}

export function labelVendorPaymentTerm(value: unknown): string {
  return labelVendorCode(value, vendorLabels.paymentTerm);
}

export function labelVendorPaymentMethod(value: unknown): string {
  return labelVendorCode(value, vendorLabels.paymentMethod);
}

export function labelVendorDeliveryScope(value: unknown): string {
  return labelVendorCode(value, vendorLabels.deliveryScope);
}

export function labelVendorDeliveryCycle(value: unknown): string {
  return labelVendorCode(value, vendorLabels.deliveryCycle);
}

export function labelVendorFreightRule(value: unknown): string {
  return labelVendorCode(value, vendorLabels.freightRule);
}

export function labelVendorReturnHandling(value: unknown): string {
  return labelVendorCode(value, vendorLabels.returnHandling);
}

export function labelVendorReturnCondition(value: unknown): string {
  return labelVendorCode(value, vendorLabels.returnCondition);
}

export function labelVendorReturnFreight(value: unknown): string {
  return labelVendorCode(value, vendorLabels.returnFreight);
}

export function labelVendorInvoiceFormat(value: unknown): string {
  return labelVendorCode(value, vendorLabels.invoiceFormat);
}

export function labelVendorInvoiceDelivery(value: unknown): string {
  return labelVendorCode(value, vendorLabels.invoiceDelivery);
}

export function labelVendorCategory(value: unknown): string {
  return labelVendorCode(value, vendorLabels.category, '未設定');
}

export function formatVendorCategories(value: unknown): string {
  return labelList(value, vendorLabels.category);
}

export function formatVendorWeekdays(value: unknown): string {
  return labelList(value, vendorLabels.weekday);
}

export function parseVendorWeekdays(value: unknown): string[] {
  return codeList(value, vendorLabels.weekday);
}

export function formatVendorReturnConditions(value: unknown): string {
  return labelList(value, vendorLabels.returnCondition);
}

export function parseVendorReturnConditions(value: unknown): string[] {
  return codeList(value, vendorLabels.returnCondition);
}

export function labelVendorStatus(value: unknown): string {
  return labelVendorCode(value, vendorLabels.vendorStatus);
}

export function labelVendorChangeRequestStatus(value: unknown): string {
  return labelVendorCode(value, vendorLabels.changeRequestStatus);
}

export function labelVendorChangedField(value: unknown): string {
  return labelVendorCode(value, vendorLabels.changeRequestField, '資料欄位');
}

export function labelVendorAttachmentType(value: unknown): string {
  return labelVendorCode(value, vendorLabels.attachmentType, '其他附件');
}

export function formatVendorMoney(value: unknown, currency = 'TWD'): string {
  if (value === null || value === undefined || value === '') return '未填寫';
  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount)) return '未填寫';
  const formatted = new Intl.NumberFormat('zh-TW', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
  return currency === 'TWD' ? `NT$ ${formatted}` : `${formatted} ${currency}`;
}

export function formatVendorDate(value: unknown, withTime = false): string {
  const raw = normalized(value);
  if (!raw) return '未填寫';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '未填寫';
  const parts = new Intl.DateTimeFormat('zh-TW', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const dateText = `${values.year}/${values.month}/${values.day}`;
  return withTime && values.hour && values.minute
    ? `${dateText} ${values.hour}:${values.minute}`
    : dateText;
}

export function formatVendorTime(value: unknown): string {
  const raw = normalized(value);
  if (!raw) return '未填寫';
  const match = raw.match(/(\d{1,2}:\d{2})/);
  return match ? match[1] : '未填寫';
}

export function formatVendorFileSize(value: unknown): string {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '未填寫';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
